import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { makeStyles } from '@material-ui/core/styles';
import { Content } from '@backstage/core-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CodeIcon from '@mui/icons-material/Code';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { TeamEntityV1alpha1 } from 'backend/src/custom-processors/types';
import { HealthSummary } from './HealthSummary';
import { ServiceBreakdownTable } from './ServiceBreakdownTable';
import { PRImpactTable } from './PRImpactTable';
import { UserHealthDashboard } from './UserHealthDashboard';
import { HealthLoadingState } from './HealthLoadingState';
import { HealthNoDataState } from './HealthNoDataState';
import { DateRangePicker, DateRange, parseDateRangeFromParams, updateDateRangeParams } from './DateRangePicker';
import { FrontendTeamHealth } from './FrontendTeamHealth';
import { useTeamFilter, TeamSelector } from './code';
import {
  teamMetricsApiRef,
  UIIncident,
  UICoverageByType,
  UIRepoWithTypedCoverage,
} from '../../../../../../api/teamMetricsApi';
import { IncidentsContent } from './incidents';
import { calculateWeeklyPRData, calculatePRSummary } from './utils';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
  },
  titleSection: {},
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: 14,
    color: '#8E9299',
  },
  divider: {
    borderTop: '1px solid #E2E8F0',
    margin: theme.spacing(4, 0),
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
  },
  infoIcon: {
    fontSize: 16,
    color: '#3182CE',
    marginTop: 2,
  },
  infoText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.5,
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    borderBottom: '1px solid #E2E8F0',
  },
  tab: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 14,
    minHeight: 48,
    '&.Mui-selected': {
      color: '#0070F3',
    },
  },
  tabIcon: {
    marginRight: theme.spacing(1),
    fontSize: 18,
  },
  chartSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0, 4),
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  chartContainer: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    padding: theme.spacing(2),
  },
  chartWrapper: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  chartArea: {
    flex: 1,
    height: 200,
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minWidth: 180,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  legendLabel: {
    fontSize: 12,
    color: '#4A5568',
    flex: 1,
  },
  legendValue: {
    fontSize: 12,
    fontWeight: 600,
    color: '#1A1A1A',
  },
  legendDivider: {
    borderTop: '1px solid #E2E8F0',
    margin: theme.spacing(1, 0),
  },
}));

interface CodeHealthState {
  loading: boolean;
  error: Error | null;
  teamCoverageByType: UICoverageByType[];
  orgCoverageByType: UICoverageByType[];
  repoSummaries: UIRepoWithTypedCoverage[];
}

interface TypedPRsState {
  loading: boolean;
  error: Error | null;
  prs: import('../../../../../../api/teamMetricsApi').UIPRWithTypedCoverage[];
}

const HealthContent: React.FC = () => {
  const { entity } = useEntity<TeamEntityV1alpha1>();
  const teamName = entity?.metadata?.name || '';

  // Frontend team gets a custom health page
  if (teamName === 'acl-dev-team-frontend') {
    return <FrontendTeamHealth />;
  }

  return (
    <Routes>
      <Route path="/" element={<HealthOverview />} />
      <Route path="/incidents" element={<IncidentsPage />} />
      <Route path="/prs" element={<TeamPRsPage />} />
      <Route path="/user/:username" element={<UserHealthPage />} />
    </Routes>
  );
};

const UserHealthPage: React.FC = () => {
  const classes = useStyles();
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  if (!username) {
    return <HealthNoDataState context="user" entityName="Unknown" />;
  }

  return (
    <Content className={classes.root}>
      <UserHealthDashboard
        weaveEmail={decodeURIComponent(username)}
        onBack={handleBack}
      />
    </Content>
  );
};

interface IncidentsState {
  loading: boolean;
  error: Error | null;
  incidents: UIIncident[];
  columnHeaders: string[];
  hasFetched: boolean;
}

const IncidentsPage: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity<TeamEntityV1alpha1>();
  const teamMetricsApi = useApi(teamMetricsApiRef);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamName = entity?.metadata?.name || '';

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const parsed = parseDateRangeFromParams(searchParams);
    return parsed;
  });

  const [incidentsState, setIncidentsState] = useState<IncidentsState>({
    loading: true,
    error: null,
    incidents: [],
    columnHeaders: [],
    hasFetched: false,
  });

  // Sync date range to URL
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('start_date', dateRange.start);
    setSearchParams(newParams, { replace: true });
  }, [dateRange.start, searchParams, setSearchParams]);

  // Fetch incidents
  useEffect(() => {
    let cancelled = false;

    const fetchIncidents = async () => {
      setIncidentsState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await teamMetricsApi.GetIncidents({
          startDate: dateRange.start,
          endDate: new Date().toISOString().split('T')[0],
        });

        if (!cancelled) {
          const incidentsList = response.incidents || [];
          // Use columnHeaders from API response to preserve spreadsheet column order
          const headers = response.columnHeaders ||
            (incidentsList.length > 0 ? Object.keys(incidentsList[0].fields || {}) : []);

          setIncidentsState({
            loading: false,
            error: null,
            incidents: incidentsList.map((inc) => ({
              fields: inc.fields || {},
              spreadsheetRowLink: inc.spreadsheetRowLink || '',
            })),
            columnHeaders: headers,
            hasFetched: true,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setIncidentsState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch incidents'),
            hasFetched: true,
          }));
        }
      }
    };

    fetchIncidents();

    return () => {
      cancelled = true;
    };
  }, [dateRange.start, teamMetricsApi]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'code' | 'incidents') => {
    if (newValue === 'code') {
      // Navigate back to main health page preserving date range
      navigate(`..?start_date=${dateRange.start}`);
    }
    // If already on incidents, do nothing
  };

  return (
    <Content className={classes.root}>
      <Box padding={4}>
        <Box className={classes.header}>
          <Box className={classes.titleSection}>
            <Typography className={classes.title}>Team Health</Typography>
            <Typography className={classes.subtitle}>
              Code coverage and incident metrics
            </Typography>
          </Box>

          <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
        </Box>

        <Box className={classes.tabsContainer}>
          <Tabs
            value="incidents"
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="code"
              label={
                <Box display="flex" alignItems="center">
                  <CodeIcon className={classes.tabIcon} />
                  Code
                </Box>
              }
              className={classes.tab}
            />
            <Tab
              value="incidents"
              label={
                <Box display="flex" alignItems="center">
                  <WarningAmberIcon className={classes.tabIcon} />
                  Incidents
                </Box>
              }
              className={classes.tab}
            />
          </Tabs>
        </Box>

        {incidentsState.loading ? (
          <HealthLoadingState />
        ) : incidentsState.error ? (
          <HealthNoDataState context="incidents" entityName={teamName} />
        ) : (
          <IncidentsContent
            incidents={incidentsState.incidents}
            columnHeaders={incidentsState.columnHeaders}
            startDate={dateRange.start}
            teamName={teamName}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}
      </Box>
    </Content>
  );
};

const TeamPRsPage: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity<TeamEntityV1alpha1>();
  const teamMetricsApi = useApi(teamMetricsApiRef);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamName = entity?.metadata?.name || '';

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const parsed = parseDateRangeFromParams(new URLSearchParams(window.location.search));
    updateDateRangeParams(parsed);
    return parsed;
  });

  // Team filter for multi-team PR report
  const teamFilter = useTeamFilter({
    initialTeam: teamName,
    searchParams,
    setSearchParams,
  });

  const [selectedUserFilter, setSelectedUserFilter] = useState<string[]>([]);
  const [prsState, setPrsState] = useState<TypedPRsState>({
    loading: true,
    error: null,
    prs: [],
  });

  useEffect(() => {
    let cancelled = false;

    const fetchPRs = async () => {
      if (teamFilter.selectedTeams.length === 0) return;

      setPrsState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const weaveUsers = selectedUserFilter.length > 0 ? selectedUserFilter : undefined;

        const response = await teamMetricsApi.GeneratePRReport({
          teamNames: teamFilter.selectedTeams,
          startDate: dateRange.start,
          weaveUsers,
        });

        if (!cancelled) {
          setPrsState({
            loading: false,
            error: null,
            prs: response.prs,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setPrsState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch PR data'),
          }));
        }
      }
    };

    fetchPRs();

    return () => {
      cancelled = true;
    };
  }, [teamFilter.selectedTeams, dateRange.start, teamMetricsApi, selectedUserFilter]);

  const availableUsers = useMemo(() => {
    const uniqueUsers = new Set(
      prsState.prs.map((pr) => pr.weaveUser).filter(Boolean)
    );
    return Array.from(uniqueUsers).sort();
  }, [prsState.prs]);

  const weeklyPRData = useMemo(
    () => calculateWeeklyPRData(prsState.prs, dateRange.start),
    [prsState.prs, dateRange.start],
  );

  const prSummary = useMemo(
    () => calculatePRSummary(prsState.prs),
    [prsState.prs],
  );

  const handleAuthorClick = (weaveEmail: string) => {
    navigate(`/user-health/${encodeURIComponent(weaveEmail)}`);
  };

  const handleBack = () => {
    navigate('..');
  };

  if (prsState.loading) {
    return <HealthLoadingState />;
  }

  const activityChart = (
    <Box className={classes.chartSection} sx={{ mt: 2 }}>
      <Box className={classes.chartHeader}>
        <Typography className={classes.chartTitle}>PR Activity</Typography>
      </Box>
      <Paper className={classes.chartContainer} elevation={0}>
        <Box className={classes.chartWrapper}>
          <Box className={classes.chartArea}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyPRData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis
                  dataKey="week"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#8E9299' }}
                  interval={Math.max(0, Math.floor(weeklyPRData.length / 8))}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#8E9299' }}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  contentStyle={{
                    borderRadius: 4,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: 12,
                  }}
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = {
                      improved: 'Improved Coverage',
                      decreased: 'Decreased Coverage',
                      neutral: 'No Change',
                    };
                    return [`${value} PRs`, labels[name] || name];
                  }}
                />
                <Bar dataKey="improved" stackId="prs" fill="#68D391" radius={[0, 0, 0, 0]} />
                <Bar dataKey="neutral" stackId="prs" fill="#CBD5E0" radius={[0, 0, 0, 0]} />
                <Bar dataKey="decreased" stackId="prs" fill="#FC8181" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box className={classes.legend}>
            <Box className={classes.legendItem}>
              <Box className={classes.legendDot} style={{ backgroundColor: '#68D391' }} />
              <Typography className={classes.legendLabel}>Improved Coverage</Typography>
              <Typography className={classes.legendValue}>{prSummary.improved}</Typography>
            </Box>
            <Box className={classes.legendItem}>
              <Box className={classes.legendDot} style={{ backgroundColor: '#CBD5E0' }} />
              <Typography className={classes.legendLabel}>No Change</Typography>
              <Typography className={classes.legendValue}>{prSummary.neutral}</Typography>
            </Box>
            <Box className={classes.legendItem}>
              <Box className={classes.legendDot} style={{ backgroundColor: '#FC8181' }} />
              <Typography className={classes.legendLabel}>Decreased Coverage</Typography>
              <Typography className={classes.legendValue}>{prSummary.decreased}</Typography>
            </Box>
            <Box className={classes.legendDivider} />
            <Box className={classes.legendItem}>
              <Typography className={classes.legendLabel} style={{ fontWeight: 700 }}>Total PRs</Typography>
              <Typography className={classes.legendValue}>{prSummary.total}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Content className={classes.root}>
      <Box sx={{ mb: 3, px: 4, pt: 4 }}>
        <TeamSelector
          selectedTeams={teamFilter.selectedTeams}
          availableTeams={teamFilter.availableTeams}
          isLoading={teamFilter.isLoading}
          onAddTeam={teamFilter.addTeam}
          onRemoveTeam={teamFilter.removeTeam}
          canRemoveTeam={teamFilter.canRemoveTeam}
          isTeamDisabled={teamFilter.isTeamDisabled}
          onSetTeams={teamFilter.setTeams}
        />
      </Box>

      <PRImpactTable
        typedPrs={prsState.prs}
        title="All Team Pull Requests"
        subtitle="Detailed view of code coverage changes per Pull Request"
        onBack={handleBack}
        dateRangePicker={<DateRangePicker dateRange={dateRange} onChange={setDateRange} />}
        onAuthorClick={handleAuthorClick}
        availableUsers={availableUsers}
        selectedUsers={selectedUserFilter}
        onSelectedUsersChange={setSelectedUserFilter}
        activityChart={activityChart}
      />
    </Content>
  );
};

const HealthOverview: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity<TeamEntityV1alpha1>();
  const teamMetricsApi = useApi(teamMetricsApiRef);
  const alertApi = useApi(alertApiRef);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamName = entity?.metadata?.name || '';

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const parsed = parseDateRangeFromParams(new URLSearchParams(window.location.search));
    updateDateRangeParams(parsed);
    return parsed;
  });

  // Team filter for multi-team selection
  const teamFilter = useTeamFilter({
    initialTeam: teamName,
    searchParams,
    setSearchParams,
  });

  // Code health state (multi-team, multi-test-type)
  const [codeHealthState, setCodeHealthState] = useState<CodeHealthState>({
    loading: true,
    error: null,
    teamCoverageByType: [],
    orgCoverageByType: [],
    repoSummaries: [],
  });

  const [activeTab, setActiveTab] = useState<'code' | 'incidents'>('code');

  const [isExcluding, setIsExcluding] = useState(false);

  // Fetch code health data (multi-team, multi-test-type)
  useEffect(() => {
    let cancelled = false;

    const fetchCodeHealth = async () => {
      if (teamFilter.selectedTeams.length === 0) return;

      setCodeHealthState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await teamMetricsApi.GenerateCodeHealthReport({
          teamNames: teamFilter.selectedTeams,
          startDate: dateRange.start,
        });

        if (!cancelled) {
          setCodeHealthState({
            loading: false,
            error: null,
            teamCoverageByType: response.teamCoverageByType,
            orgCoverageByType: response.orgCoverageByType,
            repoSummaries: response.repoSummaries,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setCodeHealthState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch code health data'),
          }));
        }
      }
    };

    fetchCodeHealth();

    return () => {
      cancelled = true;
    };
  }, [teamFilter.selectedTeams, dateRange.start, teamMetricsApi]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'code' | 'incidents') => {
    if (newValue === 'incidents') {
      // Navigate to incidents page with current date range
      navigate(`incidents?start_date=${dateRange.start}`);
    } else {
      setActiveTab(newValue);
    }
  };

  const handlePRImpactClick = () => {
    const params = new URLSearchParams();
    params.set('teams', teamFilter.selectedTeams.join(','));
    params.set('start_date', dateRange.start);
    navigate(`prs?${params.toString()}`);
  };

  const refreshCodeHealth = async () => {
    const response = await teamMetricsApi.GenerateCodeHealthReport({
      teamNames: teamFilter.selectedTeams,
      startDate: dateRange.start,
    });

    setCodeHealthState({
      loading: false,
      error: null,
      teamCoverageByType: response.teamCoverageByType,
      orgCoverageByType: response.orgCoverageByType,
      repoSummaries: response.repoSummaries,
    });
  };

  const handleExcludeRepo = async (repoName: string, reason: string) => {
    setIsExcluding(true);
    try {
      await teamMetricsApi.AddRepositoryExclusions({
        repoNames: [repoName],
        reason,
      });

      await refreshCodeHealth();

      alertApi.post({
        message: `${repoName} has been excluded from coverage calculations`,
        severity: 'success',
        display: 'transient',
      });
    } catch (err) {
      alertApi.post({
        message: `Failed to exclude ${repoName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsExcluding(false);
    }
  };

  const handleIncludeRepo = async (repoName: string) => {
    setIsExcluding(true);
    try {
      await teamMetricsApi.RemoveRepositoryExclusions({
        repoNames: [repoName],
      });

      await refreshCodeHealth();

      alertApi.post({
        message: `${repoName} has been included in coverage calculations`,
        severity: 'success',
        display: 'transient',
      });
    } catch (err) {
      alertApi.post({
        message: `Failed to include ${repoName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsExcluding(false);
    }
  };

  const isLoading = codeHealthState.loading;
  const hasError = codeHealthState.error;
  const showMultiTeam = teamFilter.selectedTeams.length > 1;

  if (isLoading) {
    return <HealthLoadingState />;
  }

  if (hasError) {
    return <HealthNoDataState context="team" entityName={teamName} />;
  }

  return (
    <Content className={classes.root}>
      <Box padding={4}>
        <Box className={classes.header}>
          <Box className={classes.titleSection}>
            <Typography className={classes.title}>Team Health</Typography>
            <Typography className={classes.subtitle}>
              Code coverage and incident metrics
            </Typography>
          </Box>

          <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
        </Box>

        <Box className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="code"
              label={
                <Box display="flex" alignItems="center">
                  <CodeIcon className={classes.tabIcon} />
                  Code
                </Box>
              }
              className={classes.tab}
            />
            <Tab
              value="incidents"
              label={
                <Box display="flex" alignItems="center">
                  <WarningAmberIcon className={classes.tabIcon} />
                  Incidents
                </Box>
              }
              className={classes.tab}
            />
          </Tabs>
        </Box>

        {activeTab === 'code' && (
          <>
            <Box className={classes.infoBox} sx={{ mb: 3 }}>
              <InfoOutlinedIcon className={classes.infoIcon} />
              <Typography className={classes.infoText}>
                <strong>Data Source:</strong> These metrics are pulled from GitHub PRs merged to the default branch of a repo, 
                coverage data from test-coverage-api, and deployment data from the-deployer.
              </Typography>
            </Box>

            <TeamSelector
              selectedTeams={teamFilter.selectedTeams}
              availableTeams={teamFilter.availableTeams}
              isLoading={teamFilter.isLoading}
              onAddTeam={teamFilter.addTeam}
              onRemoveTeam={teamFilter.removeTeam}
              canRemoveTeam={teamFilter.canRemoveTeam}
              isTeamDisabled={teamFilter.isTeamDisabled}
              onSetTeams={teamFilter.setTeams}
            />

            <HealthSummary
              teamCoverageByType={codeHealthState.teamCoverageByType}
              orgCoverageByType={codeHealthState.orgCoverageByType}
              repoSummaries={codeHealthState.repoSummaries}
              onPRImpactClick={handlePRImpactClick}
            />

            <Box className={classes.divider} />

            <ServiceBreakdownTable
              repoSummaries={codeHealthState.repoSummaries}
              onExcludeRepo={handleExcludeRepo}
              onIncludeRepo={handleIncludeRepo}
              isExcluding={isExcluding}
              showTeamColumn={showMultiTeam}
            />
          </>
        )}

      </Box>
    </Content>
  );
};

export default HealthContent;
